import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { SignHelper } from 'src/app/helpers/sign-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { IPBoardPlacementTypeChoices } from 'src/app/model/product/ip-board/ip-board-placement-type';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import { ImmoTableIpBoardPart } from './model/immo-table-ip-board-part';
import { ImmoTableProject } from './model/immo-table-project';
import { ImmoTableSubProject } from './model/immo-table-sub-project';

export class ImmoTableHelper {
  private constructor() {}

  static readonly EMPTY_RESULT: ImmoTableProcessResult = {
    baseProjects: [],
    adjustments: [],
    soldRenteds: [],
    repos: []
  };

  static processSubProjects(
    subProjects: ImmoTableSubProject[]
  ): ImmoTableProcessResult {
    if (!CollectionHelper.isPopulated(subProjects)) {
      return this.EMPTY_RESULT;
    }
    const result: ImmoTableProcessResult = {
      baseProjects: [],
      adjustments: [],
      soldRenteds: [],
      repos: []
    };

    subProjects.forEach(sp => {
      this.processSubProject(sp);
      switch (sp.type) {
        case SUB_PROJECT_TYPES.BASE_PROJECT:
          result.baseProjects.push(sp);
          break;
        case SUB_PROJECT_TYPES.ADJUSTMENT:
          result.adjustments.push(sp);
          break;
        case SUB_PROJECT_TYPES.SOLDRENTED:
          result.soldRenteds.push(sp);
          break;
        case SUB_PROJECT_TYPES.REPO:
          result.repos.push(sp);
          break;
        default:
          throw new Error(`unknown subproject type: ${sp.type}`);
      }
    });

    return result;
  }

  private static processSubProject(subProject: ImmoTableSubProject): void {
    if (subProject.project == null) {
      return;
    }

    if (CollectionHelper.isPopulated(subProject.project.projectContacts)) {
      this.processProjectContacts(subProject.project);
    }
    if (CollectionHelper.isPopulated(subProject.project.products)) {
      this.processProducts(subProject.project);
    }
  }

  private static processProjectContacts(project: ImmoTableProject): void {
    const companyNames = new Set<string>();

    project.projectContacts.forEach(pc => {
      if (pc.company != null && !StringHelper.isEmpty(pc.company.regularName)) {
        companyNames.add(pc.company.regularName);
      }
    });

    if (companyNames.size > 0) {
      project.companyNames = [...companyNames];
    }
  }

  private static processProducts(project: ImmoTableProject): void {
    project.products.forEach(p => {
      if (p.boardPart != null) {
        p.measurementsDisplay = this.getMeasurements(p.boardPart);
        p.typeDisplay = this.getTypeDisplay(p.boardPart);
      }
    });
  }

  private static getMeasurements(ipBoardPart: ImmoTableIpBoardPart): string {
    if (ipBoardPart.sign != null) {
      return SignHelper.getSignOrientationDisplayPipeable(
        ipBoardPart.sign.signMeasurements.width,
        ipBoardPart.sign.signMeasurements.height,
        ipBoardPart.sign.shape == null
          ? false
          : ipBoardPart.sign.shape.value ===
              SignShapeTypesChoices.UPRIGHT.value,
        true,
        false,
        false
      );
    } else {
      return StringHelper.join(
        [
          ipBoardPart.otherHeight == null
            ? null
            : ipBoardPart.otherHeight.toString(),
          ipBoardPart.otherWidth == null
            ? null
            : ipBoardPart.otherWidth.toString()
        ],
        ' x '
      );
    }
  }

  private static getTypeDisplay(ipBoardPart: ImmoTableIpBoardPart): string {
    return StringHelper.join(
      [
        ipBoardPart.boardType == null ? null : ipBoardPart.boardType.label,
        this.getPlacementType(ipBoardPart)
      ],
      ' '
    );
  }

  private static getPlacementType(ipBoardPart: ImmoTableIpBoardPart): string {
    if (ipBoardPart == null) {
      return null;
    }
    return ipBoardPart.placementType == null ||
      ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.OTHER.ordinal
      ? ipBoardPart.alternativePlacementType
      : ipBoardPart.placementType.label.toLowerCase();
  }
}

export interface ImmoTableProcessResult {
  baseProjects: ImmoTableSubProject[];
  adjustments: ImmoTableSubProject[];
  soldRenteds: ImmoTableSubProject[];
  repos: ImmoTableSubProject[];
}
